import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';
import { accountTypeLabel } from '../config/audience';
import type { InvitePublic } from '../types/auth';
import {
  AuthButton,
  AuthError,
  AuthInput,
  AuthPageShell,
} from '../features/user-dashboard/auth/AuthPageLayout';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = (searchParams.get('token') || '').trim();

  const [invite, setInvite] = useState<InvitePublic | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loadingInvite, setLoadingInvite] = useState(true);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organization, setOrganization] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('Missing invitation token. Open the link from your email.');
      setLoadingInvite(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingInvite(true);
      setLoadError('');
      try {
        const data = await authService.getInvite(token);
        if (!cancelled) {
          setInvite(data);
          if (data.organization) setOrganization(data.organization);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
          setLoadError(typeof detail === 'string' ? detail : 'This invitation link is invalid or has expired.');
        }
      } finally {
        if (!cancelled) setLoadingInvite(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const roleLabel = useMemo(() => {
    if (!invite) return '';
    return accountTypeLabel(invite.role, invite.sub_role);
  }, [invite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await authService.acceptInvite({
        token,
        password,
        full_name: fullName.trim(),
        organization: organization.trim() || undefined,
      });
      setDone(true);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSubmitError(typeof detail === 'string' ? detail : 'Could not create your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  if (loadError || !invite) {
    return (
      <AuthPageShell
        title="Invitation unavailable"
        subtitle="This link may have expired or already been used."
      >
        <AuthError message={loadError || 'Invitation not found.'} />
        <p className="mt-4 text-sm text-slate-600 text-center">
          Ask your administrator to send a new invite, or{' '}
          <Link to="/userdashboard" className="text-emerald-700 font-medium hover:underline">
            sign in
          </Link>{' '}
          if you already have an account.
        </p>
      </AuthPageShell>
    );
  }

  if (done) {
    return (
      <AuthPageShell
        title="Account ready"
        subtitle="Your Digisaka account has been created."
      >
        <p className="text-sm text-slate-600 text-center mb-6">
          You can sign in with <strong>{invite.email}</strong> and the password you just set.
        </p>
        <AuthButton type="button" onClick={() => navigate('/userdashboard')}>
          Go to sign in
        </AuthButton>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell
      title="Create your account"
      subtitle="You were invited to join Digisaka."
      badge={roleLabel}
      fitContentScale={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthInput
          id="invite-email"
          label="Email"
          value={invite.email}
          onChange={() => {}}
          disabled
          icon="ri-mail-line"
        />
        <AuthInput
          id="invite-name"
          label="Full name"
          value={fullName}
          onChange={setFullName}
          required
          icon="ri-user-line"
          autoComplete="name"
        />
        <AuthInput
          id="invite-org"
          label="Organization (optional)"
          value={organization}
          onChange={setOrganization}
          icon="ri-building-4-line"
        />
        <AuthInput
          id="invite-password"
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          required
          icon="ri-lock-line"
          autoComplete="new-password"
        />
        <AuthInput
          id="invite-confirm"
          label="Confirm password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          required
          icon="ri-lock-2-line"
          autoComplete="new-password"
        />
        {submitError ? <AuthError message={submitError} /> : null}
        <AuthButton type="submit" loading={submitting}>
          Create account
        </AuthButton>
      </form>
    </AuthPageShell>
  );
}
